import { mergeAttributes, Node } from '@tiptap/core';

/** HTML attribute names we preserve on input elements (safe, common ones). */
const INPUT_ATTRS = [
  'type',
  'value',
  'name',
  'placeholder',
  'class',
  'style',
  'id',
  'checked',
  'disabled',
  'readonly',
  'min',
  'max',
  'step',
  'size',
  'maxlength',
  'minlength',
  'pattern',
  'required',
  'autocomplete',
  'aria-label',
  'data-testid',
] as const;

const BOOLEAN_ATTRS = new Set(['checked', 'disabled', 'readonly', 'required']);

function getInputAttrs(dom: HTMLElement): Record<string, string | boolean | null> {
  const attrs: Record<string, string | boolean | null> = {};
  const inputEl = dom as HTMLInputElement;
  for (const key of INPUT_ATTRS) {
    if (key === 'checked') {
      if (inputEl.type === 'checkbox' || inputEl.type === 'radio') {
        attrs[key] = inputEl.checked ? true : null;
      } else {
        attrs[key] = dom.hasAttribute(key) ? true : null;
      }
      continue;
    }
    if (BOOLEAN_ATTRS.has(key)) {
      attrs[key] = dom.hasAttribute(key) ? true : null;
      continue;
    }
    const val = dom.getAttribute(key);
    if (val !== null && val !== '') {
      attrs[key] = val;
    }
  }
  return attrs;
}

function renderInputAttrs(attrs: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of INPUT_ATTRS) {
    const val = attrs[key];
    if (BOOLEAN_ATTRS.has(key)) {
      if (val === true || val === '') out[key] = '';
      continue;
    }
    if (val == null) continue;
    if (typeof val === 'string') out[key] = val;
  }
  return out;
}

/** Apply node attrs to a real input element so checkbox/radio checked state is respected. */
function applyInputAttrs(el: HTMLInputElement, attrs: Record<string, unknown>): void {
  const type = (attrs.type as string) || 'text';
  el.type = type;
  for (const key of INPUT_ATTRS) {
    if (key === 'type') continue;
    if (BOOLEAN_ATTRS.has(key)) {
      const val = attrs[key];
      if (key === 'checked') {
        el.checked = val === true;
      } else if (key === 'disabled' || key === 'readonly' || key === 'required') {
        (el as HTMLInputElement & { [k: string]: unknown })[key] = val === true;
      }
      continue;
    }
    const val = attrs[key];
    if (val != null && typeof val === 'string') {
      el.setAttribute(key, val);
    }
  }
}

export const InputNode = Node.create({
  name: 'inputNode',

  group: 'inline',

  inline: true,

  atom: true,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    const result: Record<
      string,
      {
        default: string | boolean | null;
        parseHTML?: (el: HTMLElement) => boolean | null;
        renderHTML?: (attrs: { [k: string]: unknown }) => object;
      }
    > = {};
    for (const key of INPUT_ATTRS) {
      if (BOOLEAN_ATTRS.has(key)) {
        result[key] = {
          default: null,
          parseHTML: (el: HTMLElement) => {
            if (key === 'checked') {
              const input = el as HTMLInputElement;
              if (input.type === 'checkbox' || input.type === 'radio') {
                return input.checked ? true : null;
              }
            }
            return el.hasAttribute(key) ? true : null;
          },
          renderHTML: (attrs) => (attrs[key] === true ? { [key]: '' } : {}),
        };
      } else {
        result[key] = { default: null };
      }
    }
    return result;
  },

  parseHTML() {
    return [
      {
        tag: 'input',
        getAttrs: (dom: HTMLElement) => {
          // Do not parse inputs inside TipTap TaskItem; TaskItem has its own NodeView that
          // renders the checkbox. The saved HTML also contains a literal <input>, so parsing
          // it here would create a duplicate checkbox when content is reloaded (setContent).
          if (dom.closest('ul[data-type="taskList"]') || dom.closest('li[data-type="taskItem"]')) {
            return false;
          }
          return getInputAttrs(dom);
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = renderInputAttrs({
      ...this.options.HTMLAttributes,
      ...node.attrs,
      ...HTMLAttributes,
    });
    return ['input', mergeAttributes(attrs)];
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const el = document.createElement('input');
      const attrs = { ...this.options.HTMLAttributes, ...node.attrs };
      applyInputAttrs(el, attrs);

      el.addEventListener('change', () => {
        if (node.attrs.type !== 'checkbox' && node.attrs.type !== 'radio') return;
        const pos = getPos();
        if (typeof pos !== 'number') return;
        const { state, view } = editor;
        const tr = state.tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          checked: el.checked,
        });
        view.dispatch(tr);
      });

      return {
        dom: el,
        update: (updatedNode) => {
          if (updatedNode.type !== node.type) return false;
          applyInputAttrs(el, { ...this.options.HTMLAttributes, ...updatedNode.attrs });
          return true;
        },
      };
    };
  },
});

export default InputNode;
